import {
  MODEL_SORT_FIELDS,
  MODEL_TYPES,
  type ModelSortField,
  type ModelType,
} from '@contracts/models'
import {
  parseBaseListSearch,
  toBaseListParams,
  type BaseListSearch,
  type SortOrder,
} from '@/shared/lib/list-search'

/**
 * URL state of the model list (typed search params of the Models route, design D3). A default is
 * never written to the address: no `sort` means newest first, no `type` means every type.
 */
export interface ModelListSearch extends BaseListSearch<ModelSortField> {
  /** Ticked model types, in the order of the filter. */
  type?: ModelType[]
}

/** What the list and the summary are asked for. */
export interface ModelListParams {
  q: string | undefined
  /** `undefined`: every type (no box or every box ticked, A6). */
  modelType: ModelType[] | undefined
  sort: ModelSortField | undefined
  order: SortOrder | undefined
  page: number
  pageSize: number
}

function parseTypes(value: unknown): ModelType[] | undefined {
  const given = Array.isArray(value) ? value : value === undefined ? [] : [value]
  const types = MODEL_TYPES.filter((type) => given.includes(type))
  return types.length === 0 ? undefined : types
}

/** Tolerant parser: unknown or malformed values are dropped instead of failing the route. */
export function parseModelListSearch(search: Record<string, unknown>): ModelListSearch {
  const base = parseBaseListSearch(search, MODEL_SORT_FIELDS)
  const type = parseTypes(search.type)
  return type ? { ...base, type } : base
}

export function toModelListParams(search: ModelListSearch): ModelListParams {
  const type = search.type ?? []
  return {
    ...toBaseListParams(search),
    modelType: type.length === 0 || type.length === MODEL_TYPES.length ? undefined : type,
  }
}

/** Ticking or unticking a type of Filter by Type; the first page is shown afterwards. */
export function toggleType(
  search: Pick<ModelListSearch, 'type'>,
  type: ModelType,
  checked: boolean,
): Pick<ModelListSearch, 'type' | 'page'> {
  const current = search.type ?? []
  const next = MODEL_TYPES.filter((candidate) =>
    candidate === type ? checked : current.includes(candidate),
  )
  return { type: next.length === 0 ? undefined : next, page: undefined }
}
